import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import { SpinboxAddToCart } from 'components/Blocks/Product/AddToCart/SpinboxAddToCart';
import ProductAvailableStoresCount, {
    getProductAvailabilityEShopTwClass,
} from 'components/Blocks/Product/Availability/ProductAvailableStoresCount';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import { isB2bDomain, isB2cDomain } from 'helpers/isDomainLocale';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useDomainConfig } from 'hooks/useDomainConfig';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum } from 'types/availability';
import { GtmListNameType } from 'types/gtm';
import { ListedProductType } from 'types/product';
import { onClickProductDetailGtmEventHandler } from 'utils/Gtm/EventHandlers';

type ItemProps = {
    item: ListedProductType;
    gtmListName: GtmListNameType;
    listIndex: number;
};

const TEST_IDENTIFIER = 'pages-category-promoted-item-';

export const Item: FC<ItemProps> = ({ item, gtmListName, listIndex }) => {
    const domainConfig = useDomainConfig();
    const { t } = useTranslation();
    const { formatPriceNumber, formatPriceObject } = useFormatPrice();
    const isSpecialPrice =
        getSellingPriceByDomain(item.price, domainConfig) < getSellingPriceByDomain(item.prices.basic, domainConfig);
    const isElectronicGiftVoucher = item.isElectronicGiftVoucher;

    const onProductClickHandler = useCallback(
        (product: ListedProductType, index: number) => () => {
            onClickProductDetailGtmEventHandler(product, gtmListName, index, domainConfig.url);
        },
        [gtmListName, domainConfig.url],
    );

    return (
        <div className="relative flex flex-row flex-wrap items-center border-t border-grayLighter py-4 first-of-type:border-0">
            <div className="flex w-20 items-center pr-5" data-testid={TEST_IDENTIFIER + 'image'}>
                <ExtendedNextLink
                    type="product"
                    href={item.slug}
                    className="flex h-20 w-20 items-center justify-center"
                >
                    <Image src={item.image} alt={item.fullName} width="60" height="46" />
                </ExtendedNextLink>
            </div>
            <div className="mr-4 flex flex-1 items-center text-center text-small font-bold">
                <div
                    className={twJoin('pr-7 text-left', !isB2bDomain(domainConfig) && 'w-3/5')}
                    data-testid={TEST_IDENTIFIER + 'name'}
                >
                    <ExtendedNextLink
                        type="product"
                        href={item.slug}
                        className="text-small font-medium leading-5 tracking-wider text-primary no-underline hover:text-primary hover:no-underline"
                        onClick={onProductClickHandler(item, listIndex)}
                    >
                        <span>{item.fullName}</span>
                    </ExtendedNextLink>
                </div>
                {isB2cDomain(domainConfig) && (
                    <div className="block w-2/5 text-left">
                        <span
                            className={getProductAvailabilityEShopTwClass(
                                item.availabilityStatus === AvailabilityStatusEnum.inStock,
                            )}
                        >
                            {item.availability.status === AvailabilityStatusEnum.inStock
                                ? t('Available in eshop')
                                : t('Unavailable in eshop')}
                        </span>
                        {!isElectronicGiftVoucher && (
                            <ProductAvailableStoresCount
                                isMainVariant={item.isMainVariant}
                                availableStoresCount={item.availableStoresCount}
                                isInCentralStore={item.availabilityStatus === AvailabilityStatusEnum.inStock}
                                deliveryDaysFromCentralStore={item.deliveryDaysFromCentralStore}
                            />
                        )}
                    </div>
                )}
                {isB2bDomain(domainConfig) && (
                    <div className="block w-2/5 text-left">
                        <ProductStock
                            quantity={item.stockQuantity}
                            minimumSellingQuantity={item.b2bMinimumProductOrder ?? 1}
                        />
                    </div>
                )}
            </div>

            {isB2cDomain(domainConfig) && (
                <div
                    className="mr-2 flex w-32 items-center justify-end text-right"
                    data-testid={TEST_IDENTIFIER + 'totalprice'}
                >
                    <div>
                        <div
                            className={twJoin(
                                'text-h6 font-bold leading-5 text-primary',
                                isSpecialPrice &&
                                    'text-small font-normal leading-4 text-primary line-through decoration-red',
                            )}
                            data-testid={TEST_IDENTIFIER + 'price'}
                        >
                            {formatPriceObject(item.prices.basic)}
                        </div>
                        {isSpecialPrice && (
                            <>
                                <div className="my-1 inline-block bg-red p-1 text-tiny leading-tight text-white">
                                    {`${t('You will save')} `}
                                    {formatPriceNumber(
                                        getSellingPriceByDomain(item.prices.basic, domainConfig) -
                                            getSellingPriceByDomain(item.price, domainConfig),
                                    )}
                                </div>
                                <div
                                    className={twJoin('text-h6 font-bold leading-5 text-red')}
                                    data-testid={TEST_IDENTIFIER + 'price-special'}
                                >
                                    {formatPriceObject(item.price)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isB2bDomain(domainConfig) && (
                <>
                    <div className="mr-2 block w-48 items-center justify-end text-right">
                        <B2bProductPrice
                            b2bPaymentDiscount={item.b2bPaymentDiscount}
                            isUnitPrice={(item.b2bMinimumProductOrder ?? 0) > 1}
                            sellingPrice={item.price}
                            basePrice={item.prices.basic}
                            variant="list"
                        />
                    </div>

                    <div className="ml-4 w-80">
                        <SpinboxAddToCart
                            spinboxIdSuffix="-promoted"
                            product={item}
                            gtmListName={gtmListName}
                            listIndex={listIndex}
                            variant="withText"
                        />
                    </div>
                </>
            )}
        </div>
    );
};
